import type {
  CurpCheck,
  CurpCheckCategoryConflict,
  CurpCheckCurpData,
  PlayerProfile,
  RegistrationCurpReviewRunResponse,
  RegistrationOrder,
} from "./api";
import { sameNameText } from "./curpClassifier";
import { formatDate } from "./dateFormat";

// Revisión de CURP vs nombre del jugador (Inscripciones, lado admin). Aquí
// vive solo la parte pura de la pantalla: clase del chip «C», textos, conteos
// y qué botones aplican. La comparación la hace el servidor; esta capa nunca
// decide si la CURP es de otra persona, solo lo explica. Avisa, nunca bloquea:
// «Listos ✓» y «Docs incompletos» no cambian por esto.

export type CurpChipClass = "is-ready" | "is-review" | "is-alert" | "is-unread" | "is-missing";

/** Color del chip «C» (regla aprobada por Eduardo): verde si coincide (o ya
 * se revisó), amarillo si se parece, rojo si parece de otra persona y gris si
 * no se pudo leer. Sin archivo, «falta» como los demás documentos. */
export function curpChipClass(check: CurpCheck | null | undefined, hasFile: boolean): CurpChipClass {
  if (!hasFile) return "is-missing";
  if (!check?.needs_review) return "is-ready";
  if (check.state === "other_person") return "is-alert";
  if (check.state === "unreadable") return "is-unread";
  return "is-review";
}

/** Texto del chip y del renglón de Documentos: el estado no depende solo del
 * color (va en aria-label y title). */
export function curpStatusText(check: CurpCheck | null | undefined, hasFile: boolean): string {
  if (!hasFile) return check?.state === "requested" ? "CURP falta · se pidió otra" : "CURP falta";
  if (!check) return "CURP lista";
  if (check.needs_review) {
    if (check.state === "other_person") return "CURP lista · por revisar: parece de otra persona";
    if (check.state === "unreadable") return "CURP lista · por revisar: no se pudo leer";
    return "CURP lista · por revisar: hay diferencias con el registro";
  }
  if (check.state === "unchecked") return "CURP lista · aún sin revisar";
  if (check.state === "matches") return "CURP lista · coincide con el registro";
  if (check.reviewed) return "CURP lista · ya revisada";
  return "CURP lista";
}

/** Filtro «CURP por revisar»: parecida, otra persona o sin leer, y nadie la
 * ha marcado revisada (D13: las que no se pudieron leer sí cuentan). */
export function curpNeedsReview(order: RegistrationOrder): boolean {
  return Boolean(order.curp_check?.needs_review);
}

/** Hay archivo, pero aún no se compara (p. ej. inscripciones de antes de
 * esta función): lo que lee «Revisar CURPs». */
export function curpUnchecked(order: RegistrationOrder): boolean {
  return order.curp_check?.state === "unchecked";
}

export function countCurpToReview(orders: RegistrationOrder[]): number {
  return orders.filter(curpNeedsReview).length;
}

export function countCurpUnchecked(orders: RegistrationOrder[]): number {
  return orders.filter(curpUnchecked).length;
}

/** El torneo maneja CURP en alguna inscripción: solo entonces se muestran el
 * filtro y «Revisar CURPs» (un torneo sin CURP no ve ruido). Cuenta la liga,
 * no has_curp_pdf: el perfil Capturista no abre CURPs ni las revisa. */
export function ordersUseCurp(orders: RegistrationOrder[]): boolean {
  return orders.some((order) => Boolean(order.curp_check || order.participant?.registration_documents?.curp_pdf_url));
}

function sexLabel(sex: string | null | undefined): string | null {
  if (sex === "male") return "Hombre";
  if (sex === "female") return "Mujer";
  return null;
}

/** Género del perfil con las palabras que ya usa la app en el registro. */
function profileGenderLabel(gender: string | null | undefined): string | null {
  if (gender === "male") return "Masculino";
  if (gender === "female") return "Femenino";
  return null;
}

function joinName(...parts: Array<string | null | undefined>): string {
  return parts.map((part) => (part ?? "").trim()).filter(Boolean).join(" ");
}

/** «La CURP dice: NOMBRE · dd/mm/aaaa · Hombre/Mujer» (Hombre/Mujer es lo
 * que trae la clave: H/M). */
export function formatCurpLine(curp: CurpCheckCurpData | null | undefined): string {
  if (!curp) return "—";
  const name = (curp.full_name ?? "").trim() || joinName(curp.first_name, curp.last_name, curp.maternal_last_name);
  const parts = [name, curp.birthdate ? formatDate(curp.birthdate) : null, sexLabel(curp.sex)].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

/** «Registrado: …» desde el perfil del jugador (no desde «Datos del
 * formulario»: esa sección puede no mostrarse). */
export function formatRegisteredLine(player: PlayerProfile | null | undefined): string {
  if (!player) return "—";
  const name = joinName(player.first_name, player.last_name, player.maternal_last_name) || (player.display_name ?? "").trim();
  const parts = [
    name || "sin nombre",
    player.birthdate ? formatDate(player.birthdate) : "sin fecha de nacimiento",
    profileGenderLabel(player.gender),
  ].filter(Boolean);
  return parts.join(" · ");
}

/** Nombre y fecha que quedarían con «Usar datos de la CURP» (el sexo no se
 * cambia: D5). */
export function formatCurpNameAndBirth(curp: CurpCheckCurpData | null | undefined): string {
  if (!curp) return "—";
  const name = joinName(curp.first_name, curp.last_name, curp.maternal_last_name) || (curp.full_name ?? "").trim();
  const parts = [name, curp.birthdate ? formatDate(curp.birthdate) : null].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

const REASON_TEXT: Record<string, string> = {
  birthdate_differs: "La fecha de nacimiento no es la misma.",
  registered_name_incomplete: "Al registro le falta el nombre o el apellido paterno.",
  given_initial_differs: "La inicial del nombre no coincide con la clave de la CURP.",
  paternal_initial_differs: "La inicial del apellido paterno no coincide con la clave de la CURP.",
  given_consonant_differs: "El nombre no coincide del todo con la clave de la CURP.",
  given_name_differs: "El nombre es otro.",
  uses_other_given_name: "Se registró con solo uno de los nombres que trae la CURP.",
  given_name_spelling: "El nombre está escrito un poco distinto.",
  given_names_differ: "Falta o sobra un nombre.",
  paternal_spelling: "El apellido paterno está escrito un poco distinto.",
  paternal_differs: "El apellido paterno no es idéntico.",
  surnames_reordered: "Los apellidos están en otro orden.",
  surnames_differ: "Los apellidos son otros.",
  maternal_differs: "El apellido materno es otro.",
  sex_differs: "El sexo no coincide.",
  name_not_readable: "No pudimos leer el nombre de la CURP para compararlo. Revísala con «Ver».",
};

const NOTE_TEXT: Record<string, string> = {
  maternal_not_registered: "La CURP trae apellido materno y el registro no (es opcional).",
  name_from_code_only: "No se pudo leer el nombre impreso; se comparó con las letras de la clave.",
};

const UNREADABLE_TEXT: Record<string, string> = {
  pdf_unreadable: "El archivo no se pudo abrir como PDF.",
  no_text: "El PDF no trae texto (parece foto o escaneo). Pide la versión digital de gob.mx o revísala con «Ver».",
  no_curp: "No encontramos una clave CURP en el PDF. Revísala con «Ver».",
  not_constancia: "El PDF no parece una constancia de CURP. Revísalo con «Ver».",
  file_missing: "No encontramos el archivo en el servidor. Pide que lo suban de nuevo.",
};

/** Motivos en español claro. Los códigos que no conocemos no se muestran
 * (nunca un código crudo en pantalla). */
export function curpReasonTexts(check: CurpCheck): string[] {
  const texts: string[] = [];
  if (check.state === "unreadable") {
    texts.push(UNREADABLE_TEXT[check.unreadable_reason ?? ""] ?? "No pudimos leer el texto del PDF. Revísala con «Ver».");
  }
  for (const reason of check.reasons ?? []) {
    const text = REASON_TEXT[reason];
    if (text && !texts.includes(text)) texts.push(text);
  }
  return texts;
}

export function curpNoteTexts(check: CurpCheck): string[] {
  const texts = (check.notes ?? []).map((note) => NOTE_TEXT[note]).filter((text): text is string => Boolean(text));
  if (check.multiple) texts.push("El PDF trae más de una CURP; se comparó con la que más se parece.");
  return texts;
}

/** Título del recuadro de la CURP en el detalle de la inscripción. */
export function curpReviewTitle(check: CurpCheck): string {
  switch (check.state) {
    case "unchecked":
      return "Aún no se revisa esta CURP";
    case "requested":
      return "Se pidió otra CURP";
    case "matches":
      return "La CURP coincide con el registro";
    case "other_person":
      return check.needs_review ? "Parece la CURP de otra persona" : "CURP revisada";
    case "similar":
      return check.needs_review ? "La CURP no coincide del todo con el registro" : "CURP revisada";
    case "unreadable":
      return check.needs_review ? "No pudimos leer esta CURP" : "CURP revisada";
    default:
      return "CURP";
  }
}

/** Tono del recuadro (clase CSS), con los mismos colores del chip «C»:
 * rojo, amarillo, gris (sin leer), verde o neutro. */
export function curpReviewTone(check: CurpCheck): "alert" | "review" | "unread" | "ok" | "neutral" {
  if (check.needs_review) {
    if (check.state === "other_person") return "alert";
    if (check.state === "unreadable") return "unread";
    return "review";
  }
  if (check.state === "matches" || check.reviewed) return "ok";
  return "neutral";
}

/** «Ya revisada por X el dd/mm/aaaa» / «Se pidió otra CURP el …». */
export function curpHistoryLine(check: CurpCheck): string | null {
  if (check.state === "requested" && check.requested) {
    const when = check.requested.at ? ` el ${formatDate(check.requested.at)}` : "";
    const who = check.requested.by_name ? ` (lo pidió ${check.requested.by_name})` : "";
    return `Se pidió otra CURP${when}${who}. Cuando la suban se revisa sola.`;
  }
  if (check.reviewed) {
    const by = check.reviewed.by_name ? ` por ${check.reviewed.by_name}` : "";
    const when = check.reviewed.at ? ` el ${formatDate(check.reviewed.at)}` : "";
    // Revisada antes, pero el registro cambió después y apareció otra
    // diferencia: el servidor la regresó a «CURP por revisar».
    if (check.needs_review) {
      return `Se revisó${by}${when}, pero el registro cambió después y ahora hay otra diferencia: revísala de nuevo.`;
    }
    return check.reviewed.action === "used_curp_data"
      ? `Se usaron los datos de la CURP${by}${when}.`
      : `Ya revisada${by}${when}: se dejó como está.`;
  }
  return null;
}

function conflictReasonLabel(reason: string): string {
  if (reason === "age") return " (por edad)";
  if (reason === "gender") return " (por rama)";
  return "";
}

/** «Sub-12 Varonil (por edad), Sub-14 Femenil (por rama)». Vacío si no hay
 * choque. */
export function categoryConflictNames(conflicts: CurpCheckCategoryConflict[] | null | undefined): string {
  return (conflicts ?? [])
    .filter((item) => item && item.name)
    .map((item) => `${item.name}${conflictReasonLabel(item.reason)}`)
    .join(", ");
}

/** «Ya no cabría en: Sub-12 Varonil (por edad).» Vacío si no hay choque. */
export function categoryWarningText(conflicts: CurpCheckCategoryConflict[] | null | undefined): string {
  const names = categoryConflictNames(conflicts);
  return names ? `Ya no cabría en: ${names}.` : "";
}

export interface CurpReviewActions {
  apply: boolean;
  request: boolean;
  ok: boolean;
}

export interface CurpReviewActionOptions {
  closed: boolean;
  subUser: boolean;
  /** Perfil registrado: «Usar datos» solo se ofrece si cambiaría algo. */
  player?: PlayerProfile | null;
}

/** Qué botones del recuadro aplican. Torneo cerrado = solo lectura. Un
 * sub-usuario no cambia datos globales del jugador (D6, igual que «Editar
 * registro»). «Está bien así» solo mientras siga por revisar. «Usar datos»
 * solo si cambiaría algo del registro (si ya se usaron y el registro no ha
 * cambiado, no se vuelve a ofrecer). */
export function curpReviewActions(check: CurpCheck | null | undefined, options: CurpReviewActionOptions): CurpReviewActions {
  const none = { apply: false, request: false, ok: false };
  if (!check || options.closed || !check.checked_url) return none;
  if (check.state !== "similar" && check.state !== "other_person" && check.state !== "unreadable") return none;
  return {
    apply:
      !options.subUser
      && check.state !== "unreadable"
      && curpApplyChanges(check.curp, options.player) !== null
      && (check.needs_review || check.reviewed?.action !== "used_curp_data"),
    request: true,
    ok: check.needs_review,
  };
}

/** Qué cambiaría «Usar datos de la CURP», igual que el servidor
 * (curp_review.curp_data_updates): el nombre solo si trae nombre(s) y
 * apellido paterno y no es ya el registrado (sin acentos ni mayúsculas); la
 * fecha si la trae y es otra. Sin `player` no compara. null si no hay nada
 * que cambiar (entonces no se ofrece el botón). */
export function curpApplyChanges(
  curp: CurpCheckCurpData | null | undefined,
  player?: PlayerProfile | null,
): string | null {
  if (!curp) return null;
  const hasName = Boolean((curp.first_name ?? "").trim() && (curp.last_name ?? "").trim());
  const sameName =
    Boolean(player)
    && sameNameText(curp.first_name, player?.first_name)
    && sameNameText(curp.last_name, player?.last_name)
    && sameNameText(curp.maternal_last_name, player?.maternal_last_name);
  const name = hasName && !sameName;
  const birth = Boolean(curp.birthdate) && (!player || (player.birthdate ?? "").slice(0, 10) !== curp.birthdate);
  if (name && birth) return "el nombre y la fecha de nacimiento";
  if (name) return "el nombre";
  if (birth) return "la fecha de nacimiento";
  return null;
}

/** Línea del recuadro cuando «Usar datos» no aparece porque el nombre y la
 * fecha ya son los de la CURP y lo único distinto es el sexo (que «Usar
 * datos» no cambia, D5). null en los demás casos. */
export function curpSexOnlyHint(check: CurpCheck | null | undefined, options: CurpReviewActionOptions): string | null {
  if (!check || options.closed || options.subUser || !check.needs_review) return null;
  if (check.state !== "similar" && check.state !== "other_person") return null;
  if (!(check.reasons ?? []).includes("sex_differs")) return null;
  if (!options.player || curpApplyChanges(check.curp, options.player) !== null) return null;
  return "El nombre y la fecha ya son los de la CURP; solo el sexo es distinto. Si está mal, corrígelo con «Editar registro».";
}

/** Pregunta de «Usar datos de la CURP» (se hace tras el clic). Dice que el
 * cambio es en TODOS sus torneos y, si aplica, dónde ya no cabría. */
export function curpApplyConfirmText(playerName: string, check: CurpCheck, player?: PlayerProfile | null): string {
  const changes = curpApplyChanges(check.curp, player) ?? "los datos";
  const conflicts = categoryWarningText(check.category_conflicts_if_applied);
  const lines = [
    `¿Usar los datos de la CURP para ${playerName}?`,
    `Quedaría: ${formatCurpNameAndBirth(check.curp)}`,
    `Esto cambia ${changes} del jugador en todos sus torneos. No se mueve de categoría.`,
  ];
  if (conflicts) lines.push(conflicts);
  if ((check.reasons ?? []).includes("sex_differs")) {
    lines.push("El sexo no se cambia; si también está mal, corrígelo con «Editar registro».");
  }
  // El diálogo pinta cada renglón como párrafo.
  return lines.join("\n\n");
}

/** Mensaje tras «Usar datos de la CURP». `applied` es lo que el servidor sí
 * cambió ("name" y/o "birthdate"). */
export function curpApplySuccessText(
  playerName: string,
  applied: ReadonlyArray<string> | null | undefined,
  warnings: CurpCheckCategoryConflict[] | null | undefined,
): string {
  const name = Boolean(applied?.includes("name"));
  const birth = Boolean(applied?.includes("birthdate"));
  const changes = name && birth ? "el nombre y la fecha de nacimiento" : name ? "el nombre" : birth ? "la fecha de nacimiento" : "los datos";
  const warning = categoryWarningText(warnings);
  const verb = changes.includes(" y ") || changes === "los datos" ? "Se actualizaron" : "Se actualizó";
  return `${verb} ${changes} de ${playerName} con los de la CURP.${warning ? ` ${warning} Cámbialo de categoría si hace falta.` : ""}`;
}

/** Lo que se agrega al mensaje «Se actualizó CURP de X.» tras subirla. */
export function curpUploadResultText(check: CurpCheck | null | undefined): string {
  if (!check) return "";
  if (check.state === "matches") return "La CURP coincide con el registro.";
  if (check.state === "other_person") return "Ojo: parece la CURP de otra persona; revísala en Documentos.";
  if (check.state === "similar") return "La CURP tiene diferencias con el registro; revísala en Documentos.";
  if (check.state === "unreadable") return "No pudimos leer el texto de la CURP; revísala con «Ver».";
  return "";
}

/** «Pedir otra CURP» procede: el torneo la pide como obligatoria y alguien
 * recibiría el aviso (cuenta en la app o correo). */
export function curpCanRequestAgain(check: CurpCheck | null | undefined): boolean {
  return Boolean(check?.can_request_again) && check?.can_notify !== false;
}

/** Explicación junto a «Pedir otra CURP» (también dice por qué está
 * desactivado). */
export function curpRequestHint(check: CurpCheck): string {
  if (!check.can_request_again) return "Este torneo no pide la CURP como obligatoria: súbela tú con «Cambiar».";
  if (check.can_notify === false) {
    return "No tiene cuenta en la app ni correo: nadie recibiría el aviso. Súbela tú con «Cambiar».";
  }
  return "Quita esta CURP del registro y le manda el aviso «Documentos pendientes» para que suba la correcta desde su cuenta. El archivo anterior se conserva.";
}

/** Mensaje tras «Pedir otra CURP», según cómo salió el aviso. Nunca muestra
 * el código crudo del correo. `in_app_recipients` = cuántas cuentas lo
 * recibieron en la app (0 = no tiene cuenta: no se dice «quedó en la app»). */
export function curpRequestResultText(
  playerName: string,
  message: { email_status?: string | null; in_app_recipients?: number | null } | null | undefined,
): string {
  const emailStatus = message?.email_status;
  const inApp = message?.in_app_recipients;
  if (inApp === 0) {
    if (emailStatus === "sent") return `Se pidió otra CURP a ${playerName}: le avisamos por correo (no tiene cuenta en la app).`;
    if (emailStatus === "no_email") {
      return `Se pidió otra CURP a ${playerName}, pero no tiene cuenta en la app ni correo: nadie recibió el aviso. Súbela tú con «Subir».`;
    }
    return `Se pidió otra CURP a ${playerName}, pero el correo no salió y no tiene cuenta en la app: nadie recibió el aviso. Reenvíalo con «Enviar documentos pendientes» o súbela tú con «Subir».`;
  }
  if (emailStatus === "sent") return `Se pidió otra CURP a ${playerName}: le avisamos en la app y por correo.`;
  if (emailStatus === "no_email") {
    return `Se pidió otra CURP a ${playerName}. No tiene correo: el aviso quedó solo dentro de la app.`;
  }
  return `Se pidió otra CURP a ${playerName}. El aviso quedó en la app, pero el correo no salió; puedes reenviarlo con «Enviar documentos pendientes».`;
}

/** Mensaje tras «Revisar CURPs». Los conteos son por archivo; los que no se
 * pudieron leer también quedan «por revisar» (D13), así que se dicen aparte
 * sin sumarlos. Si el servidor no alcanzó a leer todas (presupuesto de
 * tiempo) o el lector no respondió, pide volver a presionar. */
export function curpReviewRunMessage(result: RegistrationCurpReviewRunResponse["data"]): string {
  const checked = Math.max(0, result.checked ?? 0);
  const remaining = Math.max(0, result.remaining ?? 0);
  const sentences: string[] = [];
  if (checked > 0) {
    sentences.push(checked === 1 ? "Se revisó 1 CURP." : `Se revisaron ${checked} CURP.`);
    if (result.needs_review > 0) {
      sentences.push(`${result.needs_review} ${result.needs_review === 1 ? "quedó" : "quedaron"} en «CURP por revisar».`);
    } else if (!result.unreadable && !result.missing_files) {
      sentences.push("Todas coinciden con el registro.");
    }
    if (result.unreadable > 0) {
      sentences.push(result.unreadable === 1 ? "1 no se pudo leer." : `${result.unreadable} no se pudieron leer.`);
    }
    if (result.missing_files > 0) {
      sentences.push(`${result.missing_files} sin archivo en el servidor.`);
    }
  }
  if (result.parser_unavailable) sentences.push("El lector de PDF no respondió; intenta en un momento.");
  if (remaining > 0) sentences.push(`Faltan ${remaining}; vuelve a presionar «Revisar CURPs».`);
  if (sentences.length === 0) return "No había CURP nuevas por revisar.";
  return sentences.join(" ");
}
